import { NavigationStart } from '@angular/router';
import { Driver, driver, DriveStep } from 'driver.js';
import { filter, Subscriber, Subscription, take } from 'rxjs';

import type {
  FeatureAnnouncement,
  FeatureTourStep,
} from '@customer-portal/data-access/feature-announcements';
import {
  TOUR_POLL_INTERVAL_MS,
  TOUR_READY_TIMEOUT_MS,
} from '@customer-portal/data-access/feature-announcements';

import type { FeatureTourRunDependencies } from './feature-tour-run-dependencies.interface';

export type TourOutcome =
  | 'completed'
  | 'dismissed'
  | 'not-ready'
  | 'unavailable';

export interface FeatureTourControlLabels {
  next: string;
  previous: string;
  done: string;
}

export class FeatureTourRun {
  private readonly expectedRoute: string;
  private settled = false;
  private readinessTimer: ReturnType<typeof setTimeout> | undefined;
  private navigationTimer: ReturnType<typeof setTimeout> | undefined;
  private anchorAdvanceTimer: ReturnType<typeof setTimeout> | undefined;
  private readinessDeadline = 0;
  private readinessSubscription: Subscription | undefined;
  private routeSubscription: Subscription | undefined;
  private driverInstance: Driver | undefined;
  private activeClickCleanup: (() => void) | undefined;

  constructor(
    private readonly feature: FeatureAnnouncement,
    private readonly subscriber: Subscriber<TourOutcome>,
    private readonly dependencies: FeatureTourRunDependencies,
    private readonly onFinish: (run: FeatureTourRun) => void,
    private readonly controlLabels?: FeatureTourControlLabels,
  ) {
    const route = feature.route ?? feature.readiness?.routeKey;
    this.expectedRoute = this.normalizeRoute(route ?? dependencies.router.url);
  }

  start(): void {
    this.routeSubscription = this.dependencies.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe((event) => {
        if (this.normalizeRoute(event.url) !== this.expectedRoute) {
          this.finish('dismissed', false);
        }
      });
    this.begin().catch(() => this.finish('unavailable', false));
  }

  cancel(): void {
    this.finish('dismissed', false);
  }

  private finish(
    outcome: TourOutcome,
    markSeen: boolean,
    destroyDriver = true,
  ): void {
    if (this.settled) {
      return;
    }

    this.settled = true;
    this.clearTimers();
    this.readinessSubscription?.unsubscribe();
    this.routeSubscription?.unsubscribe();
    this.clearClickListener();

    if (markSeen) {
      this.dependencies.announcementStore.markFeatureAnnouncementSeen(
        this.feature.featureKey,
        outcome === 'completed',
      );
    }

    if (destroyDriver && this.driverInstance?.isActive()) {
      this.driverInstance.destroy();
    }

    this.onFinish(this);
    this.subscriber.next(outcome);
    this.subscriber.complete();
  }

  private async begin(): Promise<void> {
    const route = this.feature.route ?? this.feature.readiness?.routeKey;

    if (
      route &&
      this.expectedRoute !== this.normalizeRoute(this.dependencies.router.url)
    ) {
      this.navigationTimer = setTimeout(
        () => this.finish('unavailable', false),
        TOUR_READY_TIMEOUT_MS,
      );
      const navigated = await this.dependencies.router.navigateByUrl(route);
      this.clearNavigationTimer();

      if (this.settled) {
        return;
      }

      if (
        !navigated ||
        this.expectedRoute !== this.normalizeRoute(this.dependencies.router.url)
      ) {
        this.finish('unavailable', false);

        return;
      }
    }

    this.beginReadiness();
  }

  private clearNavigationTimer(): void {
    if (this.navigationTimer !== undefined) {
      clearTimeout(this.navigationTimer);
      this.navigationTimer = undefined;
    }
  }

  private beginReadiness(): void {
    this.readinessDeadline = Date.now() + TOUR_READY_TIMEOUT_MS;
    const source = this.feature.readiness?.source;

    if (!source) {
      this.waitForAnchors();

      return;
    }

    const gateway = this.dependencies.readinessGateway;

    if (!gateway) {
      this.finish('unavailable', false);

      return;
    }

    this.readinessTimer = setTimeout(
      () => this.finish('not-ready', false),
      TOUR_READY_TIMEOUT_MS,
    );
    this.readinessSubscription = gateway
      .watch(source)
      .pipe(
        filter((status) => status !== 'loading'),
        take(1),
      )
      .subscribe({
        next: (status) => {
          if (this.settled) {
            return;
          }

          this.clearReadinessTimer();

          if (status !== 'ready') {
            this.finish('unavailable', false);

            return;
          }

          this.waitForAnchors();
        },
        error: () => this.finish('unavailable', false),
      });
  }

  private clearReadinessTimer(): void {
    if (this.readinessTimer !== undefined) {
      clearTimeout(this.readinessTimer);
      this.readinessTimer = undefined;
    }
  }

  private waitForAnchors(): void {
    this.dependencies.ngZone.runOutsideAngular(() => {
      if (this.settled) {
        return;
      }

      if (this.hasReadyAnchors()) {
        this.dependencies.ngZone.run(() => this.runDriver());

        return;
      }

      const remaining = this.readinessDeadline - Date.now();

      if (remaining <= 0) {
        this.dependencies.ngZone.run(() => this.finish('not-ready', false));

        return;
      }

      this.readinessTimer = setTimeout(
        () => this.waitForAnchors(),
        Math.min(TOUR_POLL_INTERVAL_MS, remaining),
      );
    });
  }

  private hasReadyAnchors(): boolean {
    const steps = this.feature.tourSteps ?? [];
    const requiredSteps = steps.filter((step) => !step.optional);

    return (
      steps.some((step) => this.findAnchor(step.selector)) &&
      requiredSteps.every((step) => this.findAnchor(step.selector))
    );
  }

  private runDriver(): void {
    const steps = (this.feature.tourSteps ?? []).map(
      (step): DriveStep => ({
        element: () => this.findAnchor(step.selector) as Element,
        skipMissingElement: Boolean(step.optional),
        popover: {
          title: this.dependencies.transloco.translate(step.title),
          description: this.dependencies.transloco.translate(step.description),
          side: step.side,
          align: step.align,
        },
        onHighlightStarted: (element) => {
          if (!step.optional && (!element || !this.isUsableAnchor(element))) {
            this.finish('unavailable', false);
          }
        },
        onHighlighted: (element) => {
          const overlay =
            this.dependencies.document.querySelector<SVGSVGElement>(
              '.driver-overlay',
            );

          if (overlay) {
            overlay.style.pointerEvents = 'none';
          }
          this.attachClickListener(step, element);
        },
        onDeselected: () => this.clearClickListener(),
      }),
    );

    try {
      this.driverInstance = driver({
        allowClose: true,
        doneBtnText: this.controlLabels?.done,
        nextBtnText: this.controlLabels?.next,
        overlayClickBehavior: 'close',
        popoverClass: 'cp-tour-popover',
        prevBtnText: this.controlLabels?.previous,
        showButtons: ['previous', 'next', 'close'],
        skipMissingElement: false,
        steps,
        onCloseClick: () => this.finish('dismissed', true),
        onDoneClick: () => this.finish('completed', true),
        onDestroyed: () => {
          if (!this.settled) {
            this.finish('dismissed', true, false);
          }
        },
      });
      this.driverInstance.drive();
    } catch {
      this.finish('unavailable', false);
    }
  }

  private attachClickListener(
    step: FeatureTourStep,
    highlightedElement?: Element,
  ): void {
    this.clearClickListener();

    if (step.advanceOn !== 'anchor-click') {
      return;
    }

    const anchor =
      highlightedElement && this.isUsableAnchor(highlightedElement)
        ? highlightedElement
        : this.findAnchor(step.selector);

    if (!anchor) {
      return;
    }

    const onClick = (): void => {
      if (this.driverInstance?.isLastStep()) {
        return;
      }

      this.anchorAdvanceTimer = setTimeout(() => {
        this.anchorAdvanceTimer = undefined;
        this.dependencies.ngZone.run(() => this.driverInstance?.moveNext());
      }, 0);
    };
    anchor.addEventListener('click', onClick, { once: true });
    this.activeClickCleanup = () =>
      anchor.removeEventListener('click', onClick);
  }

  private clearClickListener(): void {
    this.activeClickCleanup?.();
    this.activeClickCleanup = undefined;

    if (this.anchorAdvanceTimer !== undefined) {
      clearTimeout(this.anchorAdvanceTimer);
      this.anchorAdvanceTimer = undefined;
    }
  }

  private findAnchor(selector: string): Element | null {
    try {
      return (
        Array.from(this.dependencies.document.querySelectorAll(selector)).find(
          (element) => this.isUsableAnchor(element),
        ) ?? null
      );
    } catch {
      return null;
    }
  }

  private isUsableAnchor(element: Element): boolean {
    if (
      !element.isConnected ||
      element.closest('[aria-hidden="true"], [inert]') ||
      element.getAttribute('aria-disabled') === 'true' ||
      element.matches(':disabled')
    ) {
      return false;
    }

    const style =
      this.dependencies.document.defaultView?.getComputedStyle(element);
    const rect = element.getBoundingClientRect();

    return Boolean(
      element.getClientRects().length &&
        rect.width > 0 &&
        rect.height > 0 &&
        style?.display !== 'none' &&
        style?.visibility !== 'hidden' &&
        style?.opacity !== '0',
    );
  }

  private clearTimers(): void {
    if (this.readinessTimer !== undefined) {
      clearTimeout(this.readinessTimer);
      this.readinessTimer = undefined;
    }

    if (this.navigationTimer !== undefined) {
      clearTimeout(this.navigationTimer);
      this.navigationTimer = undefined;
    }
  }

  private normalizeRoute(route: string): string {
    const path = route.split(/[?#]/, 1)[0].replace(/\/+$/, '');

    return path || '/';
  }
}
